import { Platform } from 'react-native';
import SimpleStorage from '../utils/simpleStorage';
import type { StaffMembership } from '../types/staff';

const STORAGE_KEY_PREFIX = 'user_memberships_';

function nowIso() {
  return new Date().toISOString();
}

function userIdKey(userId: string) {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

class MembershipService {
  async getMembershipsForUser(userId: string): Promise<StaffMembership[]> {
    let memberships: StaffMembership[] = [];
    try {
      const raw = await SimpleStorage.getItem(userIdKey(userId));
      if (raw) memberships = JSON.parse(raw);
    } catch {
      // ignore
    }

    // Best-effort Firestore sync
    try {
      if (Platform.OS === 'web') {
        const { collection, getDocs, doc } = await import('firebase/firestore');
        const { getWebDb } = await import('./unifiedAuthService');
        const db = getWebDb();
        if (db) {
          const snap = await getDocs(collection(doc(db, 'users', userId), 'memberships'));
          const fsMemberships = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as StaffMembership[];
          if (fsMemberships.length > 0) {
            memberships = fsMemberships;
            await SimpleStorage.setItem(userIdKey(userId), JSON.stringify(memberships));
          }
        }
      } else {
        const { getMobileFirestore, initializeFirebase } = require('./unifiedAuthService');
        initializeFirebase?.();
        const getFs = getMobileFirestore?.();
        if (typeof getFs === 'function') {
          const db = getFs();
          const snap = await db.collection('users').doc(userId).collection('memberships').get();
          const fsMemberships = snap.docs.map((d: any) => ({ id: d.id, ...d.data() })) as StaffMembership[];
          if (fsMemberships.length > 0) {
            memberships = fsMemberships;
            await SimpleStorage.setItem(userIdKey(userId), JSON.stringify(memberships));
          }
        }
      }
    } catch {
      // ignore
    }

    return (memberships || []).filter((m) => m.status !== 'revoked');
  }

  async upsertMembership(membership: StaffMembership): Promise<void> {
    const userId = membership.userId;
    const existing = await this.getMembershipsForUser(userId);
    const idx = existing.findIndex((m) => m.businessId === membership.businessId);
    const next = [...existing];
    const toSave = { ...membership, updatedAt: nowIso() };
    if (idx >= 0) next[idx] = toSave;
    else next.push(toSave);

    await SimpleStorage.setItem(userIdKey(userId), JSON.stringify(next));

    // Write to Firestore best-effort (both user and business views)
    try {
      if (Platform.OS === 'web') {
        const { doc, setDoc } = await import('firebase/firestore');
        const { getWebDb } = await import('./unifiedAuthService');
        const db = getWebDb();
        if (!db) return;
        await setDoc(doc(db, 'users', userId, 'memberships', membership.businessId), toSave, { merge: true });
        await setDoc(doc(db, 'businesses', membership.businessId, 'members', userId), toSave, { merge: true });
      } else {
        const { getMobileFirestore, initializeFirebase } = require('./unifiedAuthService');
        initializeFirebase?.();
        const getFs = getMobileFirestore?.();
        if (typeof getFs !== 'function') return;
        const db = getFs();
        await db.collection('users').doc(userId).collection('memberships').doc(membership.businessId).set(toSave, { merge: true });
        await db.collection('businesses').doc(membership.businessId).collection('members').doc(userId).set(toSave, { merge: true });
      }
    } catch {
      // ignore
    }
  }
}

export const membershipService = new MembershipService();
export default membershipService;

